import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Eye, EyeOff, Pencil, RefreshCw } from 'lucide-react';
import {
  useCreateExport,
  useDeleteSegment,
  useExports,
  useRecomputeSegment,
  useSegment,
  useSegmentMembers,
  useUpdateSegment,
  useWebhookSecret,
} from '@/api/hooks';
import { errorMessage } from '@/api/client';
import type { ExportOut, ExportType, Rule, SegmentMemberOut, SegmentOut } from '@/api/types';
import { PageHeading } from '@/app/PageHeading';
import { Alert } from '@/components/arc/alert/alert';
import { Badge } from '@/components/arc/badge/badge';
import { Button } from '@/components/arc/button/button';
import { ConfirmMorph } from '@/components/arc/confirm-morph/confirm-morph';
import { CopyButton } from '@/components/arc/copy-button/copy-button';
import { Dialog, DialogContent } from '@/components/arc/dialog/dialog';
import { Input } from '@/components/arc/input/input';
import { MetricCard } from '@/components/arc/metric-card/metric-card';
import { Skeleton } from '@/components/arc/skeleton/skeleton';
import { SortableDataTable, type DataColumn } from '@/components/arc/sortable-data-table/sortable-data-table';
import { useToastStack } from '@/components/arc/toast-stack/toast-stack';
import { ExportStatusBadge, SegmentStatusBadge } from '@/components/status/StatusBadge';
import { fmtDateTime, fmtDuration, fmtNumber, labelize } from '@/lib/format';
import { useApi, useTimezone } from '@/module/MomentProvider';
import { useHref } from '@/module/basePath';
import { PerformanceCard } from './PerformanceCard';
import styles from './SegmentDetailPage.module.css';

const EXPORTS: { type: ExportType; label: string; desc: string }[] = [
  {
    type: 'dv360_placement_list',
    label: 'DV360 placement list',
    desc: 'SDF ad group columns: paste the video ID list into Placement Targeting - YouTube Videos - Include',
  },
  {
    type: 'dv360_custom_bidding',
    label: 'DV360 custom bidding script',
    desc: 'Score-weighted script, top videos by size',
  },
  { type: 'csv', label: 'CSV', desc: 'Video, URL, title, score, topics and brands' },
  { type: 'json', label: 'JSON', desc: 'Full member list with moments' },
  { type: 'webhook', label: 'Webhook', desc: 'Signed POST to the segment webhook' },
];

type MemberRow = Record<string, unknown> & {
  key: string;
  score: number;
  title: string;
  start: number;
  m: SegmentMemberOut;
};

export function SegmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const href = useHref();
  const seg = useSegment(id);

  const back = (
    <Link to={href('/segments')} className={styles.back}>
      <ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />
      Back to segments
    </Link>
  );

  if (seg.isError) {
    return (
      <main className="page">
        <PageHeading title="Segment" above={back} />
        <div className="stack-4">
          <Alert tone="danger" title="We could not load this segment">
            {errorMessage(seg.error)}
          </Alert>
          <div>
            <Button type="button" variant="secondary" onClick={() => void seg.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </main>
    );
  }
  if (!seg.data) {
    return (
      <main className="page" aria-busy="true">
        <PageHeading title="Segment" above={back} />
        <Skeleton label="Loading segment" lines={6} />
      </main>
    );
  }
  return <SegmentDetail segment={seg.data} back={back} />;
}

function SegmentDetail({ segment: s, back }: { segment: SegmentOut; back: ReactNode }) {
  const nav = useNavigate();
  const href = useHref();
  const tz = useTimezone();
  const { toast } = useToastStack();
  const recompute = useRecomputeSegment();
  const del = useDeleteSegment();

  const runRecompute = () =>
    recompute
      .mutateAsync(s.id)
      .then(() =>
        toast({
          type: 'success',
          title: 'Recompute queued',
          description: 'Members are being rebuilt in the background.',
        }),
      )
      .catch((e) =>
        toast({ type: 'error', title: 'Could not queue recompute', description: errorMessage(e) }),
      );

  return (
    <main className="page">
      <PageHeading
        title={s.name}
        description={s.description ?? undefined}
        above={back}
        actions={
          <>
            <Button
              type="button"
              variant="secondary"
              loading={recompute.isPending}
              onClick={() => void runRecompute()}
            >
              <RefreshCw size={16} strokeWidth={1.75} aria-hidden="true" />
              Recompute
            </Button>
            <Button type="button" onClick={() => nav(href(`/segments/${s.id}/edit`))}>
              <Pencil size={16} strokeWidth={1.75} aria-hidden="true" />
              Edit segment
            </Button>
            <ConfirmMorph
              label="Delete segment"
              prompt={`Delete ${s.name}?`}
              confirmLabel="Delete"
              pendingLabel="Deleting"
              errorLabel="Could not delete (in use by a campaign?)"
              onConfirm={() => del.mutateAsync(s.id).then(() => nav(href('/segments')))}
            />
          </>
        }
        below={
          <div className={styles.meta}>
            <SegmentStatusBadge status={s.status} />
            <span className="text-secondary">
              {labelize(s.granularity)} granularity, match {s.match_mode}, minimum confidence{' '}
              {s.min_confidence.toFixed(2)}
            </span>
            <span className="text-muted">Last computed {fmtDateTime(s.last_computed_at, tz)}</span>
          </div>
        }
      />

      <div className="kpi-grid">
        <MetricCard label="Videos" value={s.video_count} context="in this segment" />
        {s.granularity === 'moment' ? (
          <MetricCard label="Moments" value={s.moment_count} context="in this segment" />
        ) : null}
        <MetricCard
          label="Rules"
          value={s.rules.include.length + s.rules.exclude.length}
          context={`${s.rules.include.length} include, ${s.rules.exclude.length} exclude`}
        />
      </div>

      <div className={styles.layout}>
        <div className="stack-6">
          <MembersSection segment={s} />
          <section className="stack-4" aria-labelledby="rules-heading">
            <h2 id="rules-heading" className={styles.sectionTitle}>
              Rules
            </h2>
            <div className={styles.rulesGrid}>
              <RulesView title="Include" rules={s.rules.include} />
              <RulesView title="Exclude" rules={s.rules.exclude} />
            </div>
          </section>
        </div>
        <div className="stack-6">
          <ExportsSection segment={s} />
          <WebhookSection segment={s} />
        </div>
      </div>
      {/* full width: the per-video table has nine numeric columns and did not fit the left column */}
      <PerformanceCard segmentId={s.id} />
    </main>
  );
}

function MembersSection({ segment: s }: { segment: SegmentOut }) {
  const href = useHref();
  const members = useSegmentMembers(s.id);
  const rows = useMemo<MemberRow[]>(
    () =>
      (members.data?.pages.flatMap((p) => p.items) ?? []).map((m) => ({
        key: `${m.video_id}-${m.moment_id ?? 'v'}`,
        score: Math.round(m.score * 100),
        title: m.video_title ?? m.video_external_id ?? m.video_id,
        start: m.start_sec ?? 0,
        m,
      })),
    [members.data],
  );

  const columns: DataColumn<MemberRow>[] = [
    {
      key: 'score',
      label: 'Score',
      numeric: true,
      width: 76,
      render: (v) => <span className={styles.score}>{String(v)}%</span>,
    },
    {
      key: 'title',
      label: 'Video',
      width: 230,
      render: (_v, r) => (
        <div className={styles.videoCell}>
          <span className={styles.thumb} aria-hidden="true">
            {r.m.thumbnail_url ? <img src={r.m.thumbnail_url} alt="" loading="lazy" /> : null}
          </span>
          <span className={styles.videoText}>
            <Link
              to={href(
                `/videos/${r.m.video_id}${r.m.start_sec != null ? `?t=${Math.floor(r.m.start_sec)}` : ''}`,
              )}
              className={styles.videoTitle}
            >
              {r.title}
            </Link>
            {r.m.channel_name ? <span className={styles.videoChannel}>{r.m.channel_name}</span> : null}
          </span>
        </div>
      ),
    },
    {
      key: 'start',
      label: 'Moment',
      width: 180,
      render: (_v, r) =>
        r.m.moment_id ? (
          <span className={styles.momentCell}>
            <span className="mono text-secondary">
              {fmtDuration(r.m.start_sec)} to {fmtDuration(r.m.end_sec)}
            </span>
            {r.m.scene_summary ? <span className={styles.momentSummary}>{r.m.scene_summary}</span> : null}
          </span>
        ) : (
          <span className="text-muted">Whole video</span>
        ),
    },
    {
      key: 'tags',
      label: 'Matched',
      sortable: false,
      width: 210,
      render: (_v, r) => (
        <span className={styles.tags}>
          {r.m.brands.slice(0, 2).map((b) => (
            <Badge key={`b-${b}`} size="sm" tone="info">
              {b}
            </Badge>
          ))}
          {r.m.topics.slice(0, 3).map((t) => (
            <Badge key={`t-${t}`} size="sm">
              {t}
            </Badge>
          ))}
        </span>
      ),
    },
  ];

  return (
    <section className="stack-4" aria-labelledby="members-heading" aria-busy={members.isLoading || undefined}>
      <div className={styles.sectionHead}>
        <h2 id="members-heading" className={styles.sectionTitle}>
          Members
        </h2>
        <span className="text-muted text-xs">
          {fmtNumber(rows.length)} of {fmtNumber(s.granularity === 'moment' ? s.moment_count : s.video_count)}{' '}
          loaded
        </span>
      </div>
      {members.isError ? (
        <div className="stack-4">
          <Alert tone="danger" title="We could not load members">
            {errorMessage(members.error)}
          </Alert>
          <div>
            <Button type="button" variant="secondary" onClick={() => void members.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : members.isLoading ? (
        <Skeleton label="Loading members" lines={5} />
      ) : (
        <>
          <div className="table-scroll">
            <SortableDataTable
              rows={rows}
              columns={columns}
              rowKey="key"
              caption="Segment members"
              emptyMessage="No members yet. Recompute the segment to build the list."
              defaultSort={{ key: 'score', direction: 'desc' }}
              itemName={{ one: 'member', other: 'members' }}
            />
          </div>
          {members.hasNextPage ? (
            <div className={styles.center}>
              <Button
                type="button"
                variant="secondary"
                loading={members.isFetchingNextPage}
                onClick={() => void members.fetchNextPage()}
              >
                Load more members
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function RulesView({ title, rules }: { title: string; rules: Rule[] }) {
  return (
    <div className={styles.ruleGroup}>
      <h3 className={styles.ruleTitle}>{title}</h3>
      {rules.length === 0 ? (
        <p className="text-muted">No conditions</p>
      ) : (
        <ul className={`${styles.ruleList} rows`}>
          {rules.map((r, i) => (
            <li key={i} className={styles.rule}>
              <Badge size="sm">{labelize(r.field)}</Badge>
              <span className="text-secondary">{r.op.replace(/_/g, ' ')}</span>
              {(Array.isArray(r.value) ? r.value : [r.value]).map((v, j) => (
                <Badge key={j} size="sm" tone="info">
                  {String(v)}
                </Badge>
              ))}
              {r.threshold != null ? (
                <span className="text-muted">at least {r.threshold.toFixed(2)}</span>
              ) : null}
              {r.min_confidence != null ? (
                <span className="text-muted">confidence at least {r.min_confidence.toFixed(2)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExportsSection({ segment: s }: { segment: SegmentOut }) {
  const tz = useTimezone();
  const api = useApi();
  const { toast } = useToastStack();
  const exportsQ = useExports(s.id);
  const createExport = useCreateExport();
  const [exportError, setExportError] = useState<string | null>(null);
  const [dvOpen, setDvOpen] = useState(false);
  const [advertiserId, setAdvertiserId] = useState('');
  const [lineItemId, setLineItemId] = useState('');

  const runExport = async (type: ExportType, config: Record<string, unknown> = {}) => {
    setExportError(null);
    try {
      await createExport.mutateAsync({ segment_id: s.id, type, config });
      toast({
        type: 'success',
        title: 'Export queued',
        description: `${labelize(type)} is being generated in the background.`,
      });
      return true;
    } catch (e) {
      setExportError(errorMessage(e));
      return false;
    }
  };

  const download = async (e: ExportOut) => {
    try {
      const { url, filename } = await api.exports.downloadBlob(e.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? String(e.result?.filename ?? `${e.type}.txt`);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      toast({ type: 'error', title: 'Download failed', description: errorMessage(err) });
    }
  };

  const recent = (exportsQ.data?.items ?? []).slice(0, 8);

  return (
    <section className="stack-4" aria-labelledby="exports-heading">
      <h2 id="exports-heading" className={styles.sectionTitle}>
        Export and activate
      </h2>
      {exportError ? (
        <Alert tone="danger" title="We could not start the export" onDismiss={() => setExportError(null)}>
          {exportError}
        </Alert>
      ) : null}
      <div className={`${styles.group} rows`}>
        {EXPORTS.map((e) => {
          const blocked = e.type === 'webhook' && !s.webhook_url;
          return (
            <div key={e.type} className={styles.exportRow}>
              <div className={styles.exportText}>
                <span className={styles.exportLabel}>{e.label}</span>
                <span className={styles.exportDesc}>
                  {blocked ? 'Set a webhook URL below to enable this export.' : e.desc}
                </span>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={blocked}
                loading={createExport.isPending && createExport.variables?.type === e.type && !dvOpen}
                onClick={() => void runExport(e.type)}
              >
                Run export
              </Button>
            </div>
          );
        })}
        <div className={styles.exportRow}>
          <div className={styles.exportText}>
            <span className={styles.exportLabel}>DV360 API assign</span>
            <span className={styles.exportDesc}>
              Assign the segment videos to a line item through the DV360 API
            </span>
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={() => setDvOpen(true)}>
            Configure
          </Button>
        </div>
      </div>

      <div className="stack-4">
        <h3 className={styles.subTitle}>Recent exports</h3>
        {exportsQ.isLoading ? (
          <Skeleton label="Loading exports" lines={3} />
        ) : recent.length === 0 ? (
          <p className="text-muted">No exports yet. Run one above to see it here.</p>
        ) : (
          <ul className={`${styles.exportList} rows`}>
            {recent.map((e) => (
              <li key={e.id} className={styles.recentRow}>
                <ExportStatusBadge status={e.status} />
                <span className={styles.recentText}>
                  <span>
                    {labelize(e.type)}
                    {e.result?.row_count != null ? `, ${fmtNumber(Number(e.result.row_count))} rows` : ''}
                  </span>
                  {e.error ? <span className={styles.recentError}>{String(e.error)}</span> : null}
                </span>
                <span className="text-muted text-xs">{fmtDateTime(e.created_at, tz)}</span>
                {e.status === 'done' && e.result?.file_key ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => void download(e)}>
                    <Download size={16} strokeWidth={1.75} aria-hidden="true" />
                    Download
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={dvOpen} onOpenChange={setDvOpen}>
        <DialogContent
          title="Assign to a DV360 line item"
          description="Adds the segment videos and URLs as positive targeting on the line item."
        >
          <div className="stack-4">
            <Input
              label="Advertiser ID"
              value={advertiserId}
              onChange={(e) => setAdvertiserId(e.target.value)}
            />
            <Input label="Line item ID" value={lineItemId} onChange={(e) => setLineItemId(e.target.value)} />
            <div className={styles.dialogActions}>
              <Button type="button" variant="secondary" onClick={() => setDvOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!advertiserId.trim() || !lineItemId.trim()}
                loading={createExport.isPending && dvOpen}
                onClick={() =>
                  void runExport('dv360_placement_list', {
                    mode: 'api',
                    advertiser_id: advertiserId.trim(),
                    line_item_id: lineItemId.trim(),
                  }).then((ok) => ok && setDvOpen(false))
                }
              >
                Assign videos
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function WebhookSection({ segment: s }: { segment: SegmentOut }) {
  const update = useUpdateSegment();
  const [url, setUrl] = useState(s.webhook_url ?? '');
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const secret = useWebhookSecret(s.id, showSecret && !!s.webhook_url);
  const dirty = url.trim() !== (s.webhook_url ?? '');

  const saveUrl = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: s.id, body: { webhook_url: url.trim() || null } });
      setSaved(true);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const regenerate = async () => {
    setError(null);
    try {
      await update.mutateAsync({ id: s.id, body: { regenerate_webhook_secret: true } });
      setShowSecret(true);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <section className="stack-4" aria-labelledby="webhook-heading">
      <h2 id="webhook-heading" className={styles.sectionTitle}>
        Webhook
      </h2>
      {error ? (
        <Alert tone="danger" title="We could not update the webhook" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Input
        label="Webhook URL"
        type="url"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value);
          setSaved(false);
        }}
        placeholder="https://dco.example.com/scenewise"
        description="Receives a signed POST after every recompute."
      />
      <div className="row-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!dirty}
          loading={update.isPending && !update.variables?.body.regenerate_webhook_secret}
          onClick={() => void saveUrl()}
        >
          {saved && !dirty ? 'Saved' : 'Save webhook URL'}
        </Button>
        {s.webhook_url ? (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={() => setShowSecret((v) => !v)}>
              {showSecret ? (
                <EyeOff size={16} strokeWidth={1.75} aria-hidden="true" />
              ) : (
                <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
              )}
              {showSecret ? 'Hide secret' : 'Show secret'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              loading={update.isPending && !!update.variables?.body.regenerate_webhook_secret}
              onClick={() => void regenerate()}
            >
              Regenerate secret
            </Button>
          </>
        ) : null}
      </div>
      {s.webhook_url && showSecret ? (
        secret.isLoading ? (
          <Skeleton label="Loading secret" lines={1} />
        ) : secret.isError ? (
          <Alert tone="danger" title="We could not load the secret">
            {errorMessage(secret.error)}
          </Alert>
        ) : secret.data?.webhook_secret ? (
          <div className={styles.secret}>
            <code className={styles.secretValue}>{secret.data.webhook_secret}</code>
            <CopyButton value={secret.data.webhook_secret} label="Copy secret" variant="plain" />
          </div>
        ) : (
          <p className="text-muted">No secret yet. Regenerate one to sign deliveries.</p>
        )
      ) : null}
      {s.webhook_url ? (
        <p className="text-muted text-xs">
          Deliveries carry{' '}
          <code>X-Moment-Signature: t=timestamp,v1=hmac_sha256(secret, &quot;t.body&quot;)</code>.
        </p>
      ) : null}
    </section>
  );
}
